<template>
  <!-- Escolha do tema. A miniatura de cada cartão é pintada com as cores fixas do tema que
       ela representa, e não com as do tema corrente: o cartão precisa mostrar o que você
       vai receber, não o que você já tem. -->
  <section class="picker">
    <header class="head">
      <span v-if="step" class="lbl">{{ step }}</span>
      <h2>Como você prefere ver o Sentido?</h2>
      <p>Dá para trocar depois, a qualquer momento, em Configurações.</p>
    </header>

    <div class="options">
      <button
        v-for="option in options"
        :key="option.id"
        class="card"
        :class="{ on: chosen === option.id }"
        type="button"
        :aria-pressed="chosen === option.id"
        @click="preview(option.id)"
      >
        <span class="mini" :data-mini="option.id" aria-hidden="true">
          <span class="mini-bar"><i class="mini-brand" /><i class="mini-chip" /></span>
          <span class="mini-card"><i class="mini-line" /><i class="mini-line short" /></span>
        </span>
        <span class="foot">
          <span class="radio"><AppIcon v-if="chosen === option.id" name="check" size="sm" /></span>
          <span class="text">
            <span class="name">{{ option.name }}</span>
            <span class="about">{{ option.about }}</span>
          </span>
        </span>
      </button>

      <!-- "Seguir o sistema" não é um quarto tema: é a regra de deixar o aparelho decidir,
           e por isso tem forma de interruptor, não de cartão com miniatura. -->
      <div class="card follow">
        <span class="follow-line">
          <AppIcon name="sun" size="lg" />
          <span class="text">
            <span class="name">Seguir o sistema</span>
            <span class="about">Acompanha o claro e o escuro do aparelho</span>
          </span>
        </span>
        <AppSwitch
          :model-value="chosen === 'system'"
          label="Seguir o tema do sistema"
          @update:model-value="(on) => preview(on ? 'system' : 'dark')"
        />
      </div>
    </div>

    <p v-if="reduced" class="note">
      <AppIcon name="activity" size="sm" />
      Seu aparelho pede menos movimento, então o Sentido já entra sem as animações.
    </p>

    <button class="btn btn-cta" type="button" @click="confirm">
      continuar<AppIcon name="chevron-right" size="sm" />
    </button>
  </section>
</template>

<script>
import AppIcon from '../ui/AppIcon.vue';
import AppSwitch from '../ui/AppSwitch.vue';
import {
  apply, set, readChoice, systemReducedMotion,
} from '../../lib/theme';

export default {
  name: 'ThemePicker',
  components: { AppIcon, AppSwitch },
  props: {
    /** Rótulo do passo. Vazio quando a tela é aberta pelas Configurações. */
    step: { type: String, default: '' },
  },
  emits: ['done'],
  data() {
    return {
      /** O que estava valendo quando a tela abriu — para desfazer se ninguém confirmar. */
      before: readChoice(),
      chosen: readChoice() || 'dark',
      reduced: systemReducedMotion(),
      options: [
        { id: 'dark', name: 'Escuro', about: 'O padrão do jogo' },
        { id: 'light', name: 'Claro', about: 'Para ambientes claros' },
        { id: 'contrast', name: 'Alto contraste', about: 'Branco puro sobre preto' },
      ],
    };
  },
  methods: {
    /** Pinta na hora, sem gravar: escolher tema às cegas é escolher no escuro. */
    preview(id) {
      this.chosen = id;
      apply(id);
    },
    confirm() {
      set(this.chosen);
      this.before = this.chosen;
      this.$emit('done', this.chosen);
    },
  },
  beforeUnmount() {
    // Saiu sem confirmar: a pré-visualização não pode virar escolha por desistência.
    if (this.before !== this.chosen) apply(this.before || 'dark');
  },
};
</script>

<style scoped>
.picker { display: flex; flex-direction: column; gap: 18px; padding: 8px 4px 4px; }

.head { display: grid; gap: 7px; }
.head h2 { margin: 0; font: 700 24px/1.15 var(--f-title); color: var(--text); }
.head p { margin: 0; font: 600 13px/1.45 var(--f-body); color: var(--text-3); }

.options { display: grid; gap: 10px; grid-template-columns: 1fr 1fr; }

.card {
  display: grid;
  gap: 10px;
  padding: 10px;
  border-radius: var(--r-card);
  background: var(--veil-1);
  border: 1px solid var(--line);
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.card.on { border-color: var(--active-line); background: var(--active-veil); }

/* ── miniatura ───────────────────────────────────────────────────────────
   Cores fixas: é uma amostra do tema, não uma peça da interface. Por isso este
   é o único lugar do app em que cor literal é o certo. */
.mini {
  display: grid;
  gap: 5px;
  height: 62px;
  padding: 7px;
  border-radius: 11px;
  overflow: hidden;
}

.mini-bar { display: flex; align-items: center; gap: 5px; }
.mini-brand { width: 26px; height: 5px; border-radius: var(--r-pill); }
.mini-chip { width: 13px; height: 9px; border-radius: 3px; margin-left: auto; }
.mini-card { display: grid; gap: 4px; padding: 7px; border-radius: 7px; }
.mini-line { height: 5px; border-radius: var(--r-pill); }
.mini-line.short { width: 60%; }

[data-mini='dark'] { background: #09070E; }
[data-mini='dark'] .mini-brand { background: #FFFFFF; }
[data-mini='dark'] .mini-chip { background: #B79BFF; }
[data-mini='dark'] .mini-card { background: #141020; }
[data-mini='dark'] .mini-line { background: rgba(255, 255, 255, .55); }

[data-mini='light'] { background: #F2EFF9; }
[data-mini='light'] .mini-brand { background: #191428; }
[data-mini='light'] .mini-chip { background: #6B3AFF; }
[data-mini='light'] .mini-card { background: #FFFFFF; }
[data-mini='light'] .mini-line { background: rgba(25, 20, 40, .55); }

[data-mini='contrast'] { background: #000000; border: 1px solid rgba(255, 255, 255, .55); }
[data-mini='contrast'] .mini-brand { background: #FFFFFF; }
[data-mini='contrast'] .mini-chip { background: #00FF9C; }
[data-mini='contrast'] .mini-card { background: #0D0D12; border: 1px solid rgba(255, 255, 255, .55); }
[data-mini='contrast'] .mini-line { background: #FFFFFF; }

.foot { display: flex; align-items: flex-start; gap: 8px; }

.radio {
  width: 17px;
  height: 17px;
  margin-top: 2px;
  flex: none;
  border-radius: var(--r-pill);
  border: 1px solid var(--line-2);
  display: grid;
  place-items: center;
  color: var(--active);
}

.card.on .radio { border-color: var(--active); }

.text { min-width: 0; }
.name { display: block; font: 700 13px/1.2 var(--f-title); color: var(--text); }
.about { display: block; margin-top: 3px; font: 600 11.5px/1.3 var(--f-body); color: var(--text-3); }

/* O "seguir o sistema" ocupa a linha inteira: ele vale para os três de cima. */
.follow {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 8px 12px;
}

.follow-line { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; color: var(--text-3); }

.note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 11px 12px;
  border-radius: var(--r-card);
  background: var(--veil-1);
  border: 1px solid var(--line);
  font: 600 12px/1.4 var(--f-body);
  color: var(--text-3);
}
</style>
